import React, { useState, useMemo, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { AccessDenied } from "@/components/AccessDenied";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@musa.tech/design-system";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertCircle, RefreshCw, Home } from "lucide-react";
import type { UserWithPermissions } from "@/types/user";
import { useUsersWithKeycloak } from "@/hooks/useUsersWithKeycloak";
import { useDeleteUser } from "@/hooks/useDeleteUser";
import { CreateUserModal } from "@/components/Users/CreateUserModal";
import { UserActions } from "@/components/Users/UserActions";
import { UserFilters } from "@/components/Users/UserFilters";
import { UserTable } from "@/components/Users/UserTable";
import { useAuthContext } from "@/components/AuthProvider";
import { usePermissions } from "@/hooks/useUnifiedAuth";
import { useToast } from "@/hooks/use-toast";
import { useDemoMode } from "@/contexts/DemoModeContext";
import { maskEmail } from "@/utils/demoMode";

/**
 * MC-91: atalho do aviso de "e-mail já cadastrado" em /gestao-usuario chega
 * aqui como `?email=`. Só lido uma vez (valor inicial do filtro) — não reage
 * a mudanças futuras de `searchParams`, senão sobrescreveria o que a pessoa
 * digitar depois de abrir a tela.
 */
export const getInitialEmailFilter = (searchParams: URLSearchParams): string =>
  searchParams.get("email") || "";

const DEMO_USERS: UserWithPermissions[] = [
  {
    user_id: 'demo-user-1',
    auth_id: 'demo-auth-1',
    user_name: 'Usuário 1',
    email: 'usuario1@email.com',
    permission_role: 'admin',
    is_internal: false,
    phone_number: '(11) 99999-9999',
    created_at: '2024-01-01T00:00:00Z',
    generators: { generator_name: 'GERADOR' },
    establishments: [{ establishment_id: 'demo-est-1', establishment_name: 'ESTABELECIMENTO' }],
  },
  {
    user_id: 'demo-user-2',
    auth_id: 'demo-auth-2',
    user_name: 'Usuário 2',
    email: 'usuario2@email.com',
    permission_role: 'standard',
    is_internal: false,
    phone_number: '(11) 98888-8888',
    created_at: '2024-02-01T00:00:00Z',
    generators: { generator_name: 'GERADOR' },
    establishments: [{ establishment_id: 'demo-est-1', establishment_name: 'ESTABELECIMENTO' }],
  },
  {
    user_id: 'demo-user-3',
    auth_id: 'demo-auth-3',
    user_name: 'Usuário 3',
    email: 'usuario3@email.com',
    permission_role: 'standard',
    is_internal: false,
    phone_number: null,
    created_at: '2024-03-01T00:00:00Z',
    generators: { generator_name: 'GERADOR' },
    establishments: [],
  },
];

// Hook customizado para debounce
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const UsersKeycloak = () => {
  const { isDemoMode } = useDemoMode();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);

  // Estados para os valores digitados (atualizados em tempo real)
  const [nameFilter, setNameFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState(() => getInitialEmailFilter(searchParams));
  const [generatorFilter, setGeneratorFilter] = useState<string[]>([]);

  // Valores debounced (usados na query - só atualizam após 500ms sem digitar)
  const debouncedNameFilter = useDebounce(nameFilter, 500);
  const debouncedEmailFilter = useDebounce(emailFilter, 500);

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserWithPermissions | null>(
    null,
  );

  const pageSize = 7;

  // Buscar usuários com filtros e paginação no backend
  const { data, isLoading, error, refetch } = useUsersWithKeycloak(
    currentPage,
    pageSize,
    {
      nameFilter: debouncedNameFilter || undefined,
      emailFilter: debouncedEmailFilter || undefined,
      generatorFilter: generatorFilter.length > 0 ? generatorFilter[0] : undefined,
    },
  );
  const allUsers = isDemoMode ? DEMO_USERS : (data?.users || []);

  const { profile, isLoading: isAuthLoading } = useAuthContext();
  const { canManageUsers, isInternal } = usePermissions();
  const deleteUserMutation = useDeleteUser();
  const { toast } = useToast();

  // Check if current user should see Generator column
  const showGeneratorColumn = useMemo(() => {
    return profile?.is_internal;
  }, [profile?.is_internal]);

  const totalCount = data?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Reset to page 1 when debounced filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedNameFilter, debouncedEmailFilter, generatorFilter]);

  // Handle query params ?editar=self → redireciona para página de edição
  useEffect(() => {
    const editParam = searchParams.get("editar");
    const senhaParam = searchParams.get("senha");
    const recoveryParam = searchParams.get("recovery");

    if (editParam === "self" && profile?.user_id) {
      setSearchParams({});
      const params = new URLSearchParams({ id: profile.user_id, mode: "edit" });
      if (senhaParam === "true") params.set("senha", "true");
      if (recoveryParam === "true") params.set("recovery", "true");
      navigate(`/gestao-usuario?${params.toString()}`);
    }
  }, [searchParams, profile?.user_id, navigate, setSearchParams]);

  // Keep available role options for compatibility (even though filter is removed)
  const availableRoleOptions = useMemo(() => {
    const baseOptions = [
      { value: "all", label: "Todos os papéis" },
      { value: "admin", label: "Admin" },
      { value: "standard", label: "Standard" },
    ];

    if (profile?.is_internal) {
      baseOptions.push({ value: "internal", label: "Internal" });
    }

    return baseOptions;
  }, [profile?.is_internal]);

  const handleEditUser = (user: UserWithPermissions) => {
    navigate(`/gestao-usuario?id=${user.user_id}&mode=edit`);
  };

  const handleCreateUser = () => {
    setIsCreateModalOpen(true);
  };

  const handleMyProfile = () => {
    navigate("/meu-perfil");
  };

  const handleDeleteUser = (user: UserWithPermissions) => {
    setUserToDelete(user);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteUser = () => {
    if (userToDelete && !deleteUserMutation.isPending) {
      deleteUserMutation.mutate(
        {
          userId: userToDelete.user_id,
          email: userToDelete.email,
          role: userToDelete.permission_role,
          generatorLinks: userToDelete.generator_links,
        },
        {
          onSettled: () => {
            // Always close dialog and clear state after mutation completes (success or error)
            setIsDeleteDialogOpen(false);
            setUserToDelete(null);
          },
        },
      );
    }
  };

  // Mesma regra do item "Usuários" no menu da engrenagem e do card em
  // Configuracoes.tsx — quem não pode gerenciar usuários nem acessa a página.
  if (!isAuthLoading && !canManageUsers) {
    // Era `<Navigate to="/unauthorized">`, tela cheia fora do Layout cujo
    // único botão volta para `/` — que pode ser outra rota negada. Dentro do
    // Layout o menu fica de pé e a pessoa tem para onde ir.
    return <AccessDenied area="à gestão de usuários" />;
  }

  if (error) {
    // Limpar mensagem de erro HTML para algo mais legível
    const getErrorMessage = (errorMsg: string) => {
      if (errorMsg.includes("<!doctype html>") || errorMsg.includes("<html")) {
        return "Erro ao conectar com o servidor. O serviço pode estar temporariamente indisponível.";
      }
      if (errorMsg.includes("500")) {
        return "Erro interno do servidor. Por favor, tente novamente em alguns instantes.";
      }
      if (errorMsg.includes("404")) {
        return "Serviço não encontrado. Verifique sua conexão.";
      }
      if (errorMsg.includes("401") || errorMsg.includes("403")) {
        return "Sua sessão expirou. Por favor, faça login novamente.";
      }
      return errorMsg;
    };

    const errorMessage = getErrorMessage(error.message);

    return (
      <div className="min-h-screen bg-background p-6 flex items-center justify-center">
        <div className="max-w-2xl w-full">
          <Card className="shadow-lg border-red-200">
            <CardContent className="p-8">
              <div className="flex flex-col items-center text-center space-y-6">
                {/* Ícone de erro */}
                <div className="rounded-full bg-red-50 p-4">
                  <AlertCircle className="w-12 h-12 text-red-500" />
                </div>

                {/* Título */}
                <div className="space-y-2">
                  <h2 className="text-2xl font-bold text-gray-900">
                    Não foi possível carregar os usuários
                  </h2>
                  <p className="text-gray-600 text-base max-w-md mx-auto">
                    {errorMessage}
                  </p>
                </div>

                {/* Detalhes técnicos (collapse) */}
                {error.message !== errorMessage && (
                  <details className="w-full">
                    <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-700">
                      Ver detalhes técnicos
                    </summary>
                    <div className="mt-2 p-4 bg-gray-50 rounded-lg text-left">
                      <code className="text-xs text-gray-600 break-all">
                        {error.message.substring(0, 500)}
                        {error.message.length > 500 && "..."}
                      </code>
                    </div>
                  </details>
                )}

                {/* Ações */}
                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={() => refetch()}
                    className="flex items-center gap-2"
                    variant="default"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Tentar Novamente
                  </Button>
                  <Button
                    onClick={() => (window.location.href = "/dashboard")}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Home className="w-4 h-4" />
                    Ir para Dashboard
                  </Button>
                </div>

                {/* Dicas */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 w-full">
                  <p className="text-sm text-blue-800 font-medium mb-2">
                    💡 Possíveis soluções:
                  </p>
                  <ul className="text-sm text-blue-700 space-y-1 text-left list-disc list-inside">
                    <li>Aguarde alguns instantes e tente novamente</li>
                    <li>Verifique sua conexão com a internet</li>
                    <li>Limpe o cache do navegador (Ctrl+Shift+R)</li>
                    <li>Se o problema persistir, contate o suporte</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-6 w-full overflow-x-hidden">
      <div className="max-w-7xl mx-auto space-y-4 md:space-y-6 min-w-0">
        {/* Header */}
        <UserActions
          onCreateUser={handleCreateUser}
          onMyProfile={handleMyProfile}
          canCreateUsers={canManageUsers}
          canViewMyProfile={!isInternal}
        />

        {/* Filters and Table */}
        <Card className="bg-white border-gray-200">
          <CardContent className="pt-6">
            <UserFilters
              nameFilter={nameFilter}
              setNameFilter={setNameFilter}
              emailFilter={emailFilter}
              setEmailFilter={setEmailFilter}
              roleFilter=""
              setRoleFilter={() => {}}
              generatorFilter={generatorFilter}
              setGeneratorFilter={setGeneratorFilter}
              filteredCount={totalCount}
              totalCount={totalCount}
            />

            <UserTable
              filteredUsers={allUsers}
              showGeneratorColumn={showGeneratorColumn}
              onEditUser={handleEditUser}
              onCreateUser={handleCreateUser}
              onDeleteUser={handleDeleteUser}
              isLoading={isLoading}
            />

            {/* Pagination Controls */}
            {!isLoading && allUsers.length > 0 && (
              <div className="flex items-center justify-center mt-4 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Anterior
                  </Button>

                  {(() => {
                    const pages = [];
                    const maxVisiblePages = 5;

                    if (totalPages <= maxVisiblePages + 1) {
                      // Show all pages if total is small
                      for (let i = 1; i <= totalPages; i++) {
                        pages.push(i);
                      }
                    } else {
                      // Calculate start and end of visible range
                      let start = Math.max(
                        1,
                        currentPage - Math.floor(maxVisiblePages / 2),
                      );
                      const end = Math.min(
                        totalPages,
                        start + maxVisiblePages - 1,
                      );

                      // Adjust start if we're near the end
                      if (end === totalPages) {
                        start = Math.max(1, totalPages - maxVisiblePages + 1);
                      }

                      // Add visible pages
                      for (let i = start; i <= end; i++) {
                        pages.push(i);
                      }

                      // Add ellipsis and last page if needed
                      if (end < totalPages) {
                        pages.push("...");
                        pages.push(totalPages);
                      }
                    }

                    return pages.map((page, index) => {
                      if (page === "...") {
                        return (
                          <span
                            key={`ellipsis-${index}`}
                            className="px-2 text-gray-500"
                          >
                            ...
                          </span>
                        );
                      }

                      return (
                        <Button
                          key={page}
                          variant={currentPage === page ? "default" : "outline"}
                          size="sm"
                          onClick={() => handlePageChange(page as number)}
                        >
                          {page}
                        </Button>
                      );
                    });
                  })()}

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Próxima
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Modals */}
        <CreateUserModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
        />

        {/* Delete Confirmation Dialog */}
        <AlertDialog
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirmar remoção</AlertDialogTitle>
              <AlertDialogDescription>
                Tem certeza que deseja remover o usuário{" "}
                <strong>
                  {userToDelete?.user_name ||
                    maskEmail(userToDelete?.email, isDemoMode)}
                </strong>
                ? Esta ação não pode ser desfeita e o usuário perderá acesso ao
                sistema.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDeleteUser}
                className="bg-primary-500 hover:bg-primary-600 text-white"
                disabled={deleteUserMutation.isPending}
              >
                {deleteUserMutation.isPending ? "Removendo..." : "Remover"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default UsersKeycloak;
